import React from "react";
import { Box } from "@mui/material";
import {
  CalendarTodayOutlined,
  CategoryOutlined,
  BusinessOutlined,
  ReportProblemOutlined,
  PriorityHighOutlined,
} from "@mui/icons-material";
import Moment from "react-moment";
import { dt } from "../../utils/dt";
import { ServiceReport } from "../../models/ServiceReportsModel";
import PriceFormat from "../../../../shared/components/formats/PriceFormat";
import useTranslation from "../../../../shared/hooks/useTranslation";

const urgencyConfig: Record<number, { color: string; bg: string; labelKey: string }> = {
  1: { color: "#C0392B", bg: "rgba(192,57,43,0.08)", labelKey: "priorities.urgent" },
  2: { color: "#E67E22", bg: "rgba(230,126,34,0.08)", labelKey: "priorities.high" },
  4: { color: "#27AE60", bg: "rgba(39,174,96,0.08)", labelKey: "priorities.low" },
};

// ===================================================================
//  COMPONENT
// ===================================================================
interface SrCostCardProps {
  serviceReport: ServiceReport;
  categoryName?: string;
  isPhone: boolean;
}

const SrCostCard = ({
  serviceReport,
  categoryName,
  isPhone,
}: SrCostCardProps) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <Box
      sx={{
        background: dt.machinery.card.bg,
        border: dt.machinery.card.border,
        borderRadius: dt.machinery.card.radius,
        boxShadow: dt.machinery.card.shadow,
        padding: isPhone ? "16px" : "20px",
        fontFamily: dt.font,
        overflow: "hidden",
      }}
    >
      {/* Cost hero */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "11px",
            fontWeight: 700,
            color: dt.serviceReports.detail.sectionLabelColor,
            textTransform: "uppercase",
            letterSpacing: "0.8px",
            mb: 0.5,
          }}
        >
          {translate("cost")}
        </Box>
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: isPhone ? "20px" : "24px",
            fontWeight: 700,
            color:
              serviceReport.cost?.value > 0
                ? dt.serviceReports.detail.costHeroColor
                : dt.textMuted,
          }}
        >
          <PriceFormat value={serviceReport.cost?.value} />
        </Box>
      </Box>

      {/* Labor + Parts breakdown (if RFS-sourced) */}
      {(serviceReport.actualLaborHours != null ||
        serviceReport.actualPartsCost != null) && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
            pb: 1.5,
            borderBottom: `1px solid ${dt.serviceReports.detail.metaCardBorder}`,
          }}
        >
          {serviceReport.actualLaborHours != null && (
            <MiniStat
              label={translate("laborHours")}
              value={`${serviceReport.actualLaborHours}h`}
            />
          )}
          {serviceReport.actualPartsCost != null && (
            <MiniStat
              label={translate("partsCost")}
              value={`\u20AC${serviceReport.actualPartsCost.toFixed(2)}`}
            />
          )}
        </Box>
      )}

      {/* Metadata rows */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <MetaRow
          icon={<CalendarTodayOutlined sx={{ fontSize: 13 }} />}
          label={translate("openDate")}
        >
          {serviceReport.openDate ? (
            <Moment date={serviceReport.openDate} format="DD.MM.YYYY, HH:mm" />
          ) : (
            "-"
          )}
        </MetaRow>

        <MetaRow
          icon={<CalendarTodayOutlined sx={{ fontSize: 13 }} />}
          label={translate("closeDate")}
        >
          {serviceReport.closeDate ? (
            <Moment
              date={serviceReport.closeDate}
              format="DD.MM.YYYY, HH:mm"
            />
          ) : (
            <Box
              component="span"
              sx={{ color: dt.textMuted, fontStyle: "italic" }}
            >
              {translate("serviceReportStatus.inProgress")}
            </Box>
          )}
        </MetaRow>

        {categoryName && (
          <MetaRow
            icon={<CategoryOutlined sx={{ fontSize: 13 }} />}
            label={translate("componentAffected")}
          >
            {categoryName}
          </MetaRow>
        )}

        {serviceReport.urgency != null && urgencyConfig[serviceReport.urgency] && (
          <MetaRow
            icon={<PriorityHighOutlined sx={{ fontSize: 13 }} />}
            label={translate("urgency")}
          >
            <Box
              component="span"
              sx={{
                color: urgencyConfig[serviceReport.urgency].color,
                fontWeight: 600,
              }}
            >
              {translate(urgencyConfig[serviceReport.urgency].labelKey)}
            </Box>
          </MetaRow>
        )}

        {serviceReport.alarmCode && (
          <MetaRow
            icon={<ReportProblemOutlined sx={{ fontSize: 13 }} />}
            label={translate("alarmCodeSection")}
          >
            <Box
              component="span"
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "13px",
                fontWeight: 600,
                color: "#E74C3C",
                letterSpacing: "0.5px",
              }}
            >
              {serviceReport.alarmCode}
            </Box>
          </MetaRow>
        )}

        {serviceReport.providerCompanyName && (
          <MetaRow
            icon={<BusinessOutlined sx={{ fontSize: 13 }} />}
            label={translate("serviceProvider")}
          >
            {serviceReport.providerCompanyName}
          </MetaRow>
        )}
      </Box>
    </Box>
  );
};

// ===================================================================
//  MINI STAT (compact inline stat)
// ===================================================================
const MiniStat = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Box
      sx={{
        fontFamily: dt.font,
        fontSize: "10px",
        fontWeight: 600,
        color: dt.serviceReports.detail.sectionLabelColor,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: dt.fontMono,
        fontSize: "14px",
        fontWeight: 600,
        color: dt.textPrimary,
      }}
    >
      {value}
    </Box>
  </Box>
);

// ===================================================================
//  META ROW
// ===================================================================
const MetaRow = ({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      py: "10px",
      borderBottom: `1px solid ${dt.serviceReports.detail.metaCardBorder}`,
      "&:last-of-type": { borderBottom: "none" },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "5px",
        fontFamily: dt.font,
        fontSize: "11px",
        fontWeight: 600,
        color: dt.serviceReports.detail.sectionLabelColor,
        textTransform: "uppercase",
        letterSpacing: "0.4px",
      }}
    >
      {icon}
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: dt.fontMono,
        fontSize: "13px",
        fontWeight: 500,
        color: dt.textPrimary,
        pl: "18px",
      }}
    >
      {children}
    </Box>
  </Box>
);

export default SrCostCard;
